import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { canViewDistributors } from "./permissions";
import useAxiosPrivate from "./useAxiosPrivate";

const SEEN_KEY = "walkthrough_seen";

const PUBLIC_PATHS = ["/signin", "/register", "/reset", "/privacy-policy"];
const isPublicPath = (pathname) =>
  PUBLIC_PATHS.some(
    (pub) => pathname === pub || pathname.startsWith(pub + "/")
  );

const readSeenMap = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeSeenMap = (map) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private-mode errors */
  }
};

const WalkthroughContext = createContext(null);

export function WalkthroughProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasSite, setHasSite] = useState(false);

  const menuOpenerRef = useRef(null);
  const registerMenuOpener = useCallback((fn) => {
    menuOpenerRef.current = fn;
    return () => {
      if (menuOpenerRef.current === fn) menuOpenerRef.current = null;
    };
  }, []);
  const openMenu = useCallback(() => {
    if (typeof menuOpenerRef.current === "function") {
      menuOpenerRef.current(true);
    }
  }, []);
  const closeMenu = useCallback(() => {
    if (typeof menuOpenerRef.current === "function") {
      menuOpenerRef.current(false);
    }
  }, []);

  const isManager = Boolean(cookies?.isManager);
  const isInternalUser = Boolean(cookies?.isInternalUser);
  const isDistributor = Boolean(cookies?.isSmartriumDistributor);
  const privileged = isManager || isInternalUser || isDistributor;
  const showsDistributors = canViewDistributors(cookies?.role);

  useEffect(() => {
    if (!cookies?.user || !privileged) {
      setHasSite(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosPrivate.get(
          "/api/Site/sitesAndSystems/" + cookies.user
        );
        if (!cancelled) setHasSite(Array.isArray(res.data) && res.data.length > 0);
      } catch {
        if (!cancelled) setHasSite(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cookies?.user, privileged, axiosPrivate]);

  const steps = useMemo(() => {
    const list = [
      { key: "welcome", route: "/healthDashboard" },
      { key: "healthdashboard", route: "/healthDashboard" },
      { key: "menu", route: "/healthDashboard", openMenu: true },
      { key: "tile", route: "/notifications", demo: "tile" },
      { key: "image", route: "/notifications", demo: "image" },
    ];
    if (showsDistributors) {
      list.push({ key: "distributors", route: "/distributors" });
    }
    if (privileged) {
      list.push({ key: "managecontacts", route: "/sites" });
      if (hasSite) {
        list.push({ key: "sitecontacts", route: "/sites" });
      }
    }

    list.push({ key: "account", route: "/account" });
    list.push({ key: "settings", route: "/settings", bullets: true });
    list.push({ key: "complete", route: "/healthDashboard" });
    return list;
  }, [privileged, showsDistributors, hasSite]);

  const stepCount = steps.length;
  const step = isActive && stepIndex < stepCount ? steps[stepIndex] : null;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === stepCount - 1;

  useEffect(() => {
    if (!isActive || !step) return;
    if (step.route) navigate(step.route);
    if (step.openMenu) {
      const t = setTimeout(() => openMenu(), 50);
      return () => clearTimeout(t);
    }
    closeMenu();
  }, [isActive, step, navigate, openMenu, closeMenu]);

  useEffect(() => {
    if (!isActive) closeMenu();
  }, [isActive, closeMenu]);

  const markSeen = useCallback(() => {
    if (!cookies?.user) return;
    const map = readSeenMap();
    map[cookies.user] = true;
    writeSeenMap(map);
  }, [cookies?.user]);

  const start = useCallback(({ replay } = {}) => {
    void replay;
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const finish = useCallback(() => {
    markSeen();
    setIsActive(false);
    setStepIndex(0);
  }, [markSeen]);

  const skip = useCallback(() => {
    markSeen();
    setIsActive(false);
    setStepIndex(0);
  }, [markSeen]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= stepCount - 1) {
        markSeen();
        setIsActive(false);
        return 0;
      }
      return i + 1;
    });
  }, [stepCount, markSeen]);

  const prev = useCallback(() => {
    setStepIndex((i) => (i > 0 ? i - 1 : 0));
  }, []);

  const maybeStartForFirstLogin = useCallback(() => {
    if (!cookies?.user) return;
    const map = readSeenMap();
    if (map[cookies.user]) return;
    start({ replay: false });
  }, [cookies?.user, start]);

  const evaluatedForUserRef = useRef(null);
  useEffect(() => {
    const user = cookies?.user;
    if (!user) {
      evaluatedForUserRef.current = null;
      setIsActive(false);
      setStepIndex(0);
      return;
    }
    if (!cookies?.jwtToken || isPublicPath(location.pathname)) return;
    if (evaluatedForUserRef.current === user) return;
    evaluatedForUserRef.current = user;
    const t = setTimeout(() => maybeStartForFirstLogin(), 250);
    return () => clearTimeout(t);
  }, [
    cookies?.user,
    cookies?.jwtToken,
    location.pathname,
    maybeStartForFirstLogin,
  ]);

  const value = useMemo(
    () => ({
      isActive,
      step,
      stepIndex,
      stepCount,
      isFirst,
      isLast,
      next,
      prev,
      skip,
      finish,
      start,
      maybeStartForFirstLogin,
      registerMenuOpener,
    }),
    [
      isActive,
      step,
      stepIndex,
      stepCount,
      isFirst,
      isLast,
      next,
      prev,
      skip,
      finish,
      start,
      maybeStartForFirstLogin,
      registerMenuOpener,
    ]
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
}

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) {
    throw new Error("useWalkthrough must be used within a WalkthroughProvider");
  }
  return ctx;
}
